import React, { useState } from 'react';
import { CountyField } from './ServiceAreaFields';
import { statesForPicker, stateName } from '../shared/serviceArea';
import { moveAddressInput, moveSponsorPlan, sponsorCodeValue } from '../shared/homeMove';
import { moveHomeownerHome, acceptHomeownerSponsor } from '../utils/homeownerApi';

const STATES = statesForPicker();

function fieldStyle(colors) {
  return {
    width: '100%',
    boxSizing: 'border-box',
    minHeight: '48px',
    padding: '0.75rem 0.9rem',
    borderRadius: '0.75rem',
    border: `1px solid ${colors.border}`,
    background: colors.surface,
    color: colors.text,
    fontSize: '1rem',
    fontFamily: 'inherit',
  };
}

function choiceStyle(selected, colors, brandColor) {
  return {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.75rem',
    width: '100%',
    textAlign: 'left',
    padding: '0.9rem 1rem',
    marginBottom: '0.6rem',
    borderRadius: '0.75rem',
    border: `2px solid ${selected ? brandColor : colors.border}`,
    background: colors.surface,
    color: colors.text,
    fontFamily: 'inherit',
    fontSize: '1rem',
    cursor: 'pointer',
  };
}

export function ArchivedHomeBanner({ colors, brandColor, busy, onBack }) {
  return (
    <div
      role="status"
      style={{
        marginBottom: '1rem',
        padding: '1rem',
        borderRadius: '0.75rem',
        background: colors.surface,
        border: `1px solid ${colors.border}`,
        color: colors.text,
      }}
    >
      <p style={{ margin: '0 0 0.75rem', lineHeight: 1.45 }}>
        This home is archived. You can look through its history and documents. Changes stay on your current home.
      </p>
      <button
        type="button"
        onClick={onBack}
        disabled={busy}
        style={{
          minHeight: '48px',
          padding: '0.75rem 1rem',
          borderRadius: '0.75rem',
          border: 'none',
          background: brandColor,
          color: '#fff',
          fontWeight: 650,
          fontSize: '1rem',
          fontFamily: 'inherit',
          cursor: 'pointer',
        }}
      >
        {busy ? 'Switching…' : 'Back to your current home'}
      </button>
    </div>
  );
}

export function SponsorCodeForm({ colors, brandColor, onAccepted }) {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    const plan = moveSponsorPlan('invite', code);
    if (plan.status) {
      setError(plan.error);
      return;
    }
    setBusy(true);
    setError('');
    try {
      const result = await acceptHomeownerSponsor(plan.inviteCode);
      if (onAccepted) onAccepted(result);
    } catch (err) {
      setError((err && err.message) || 'That sponsor code is not active.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} style={{ marginTop: '1.5rem' }}>
      <h2 style={{ color: colors.text, fontWeight: 300, fontSize: '1.5rem', margin: '0 0 0.5rem' }}>
        Sponsor code
      </h2>
      <p style={{ color: colors.textSecondary, margin: '0 0 0.75rem' }}>
        If a sponsor sent you a code, enter it here. It does not create a second home.
      </p>
      <input
        aria-label="Sponsor code"
        value={code}
        onChange={(event) => setCode(sponsorCodeValue(event.target.value))}
        onPaste={(event) => {
          event.preventDefault();
          const text = event.clipboardData ? event.clipboardData.getData('text') : '';
          setCode(sponsorCodeValue(text));
        }}
        autoCapitalize="characters"
        autoComplete="off"
        spellCheck={false}
        style={fieldStyle(colors)}
      />
      {error ? <p style={{ color: '#b91c1c', margin: '0.5rem 0 0' }}>{error}</p> : null}
      <button
        type="submit"
        disabled={busy}
        style={{
          marginTop: '0.75rem',
          minHeight: '48px',
          padding: '0.75rem 1rem',
          borderRadius: '0.75rem',
          border: `2px solid ${brandColor}`,
          background: colors.surface,
          color: brandColor,
          fontWeight: 650,
          fontSize: '1rem',
          fontFamily: 'inherit',
          cursor: 'pointer',
        }}
      >
        {busy ? 'Checking…' : 'Accept sponsor'}
      </button>
    </form>
  );
}

export default function MoveHome({
  colors,
  brandColor,
  sponsored,
  onCancel,
  onMoved,
  submitMove,
}) {
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('FL');
  const [zip, setZip] = useState('');
  const [countyFips, setCountyFips] = useState('');
  const [countySource, setCountySource] = useState('');
  const [countyPending, setCountyPending] = useState(false);
  const [sponsor, setSponsor] = useState(sponsored ? 'keep' : 'sponsorless');
  const [inviteCode, setInviteCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const send = submitMove || moveHomeownerHome;

  const submit = async (event) => {
    event.preventDefault();
    const checked = moveAddressInput({
      address, city, state, zip, countyFips, countySource, countyPending,
    });
    if (checked.errors.length) {
      setError(checked.errors[0]);
      return;
    }
    const plan = moveSponsorPlan(sponsor, inviteCode);
    if (plan.status) {
      setError(plan.error);
      return;
    }
    setBusy(true);
    setError('');
    try {
      const result = await send({
        address: checked.address,
        city: checked.city,
        state: checked.state,
        zip: checked.zip,
        countyFips: checked.countyFips,
        countySource: checked.countySource,
        sponsor: plan.sponsor,
        inviteCode: plan.inviteCode,
      });
      if (onMoved) onMoved(result);
    } catch (err) {
      setError((err && err.message) || 'Could not move to that home.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="move-home" style={{ color: colors.text }}>
      <h2 style={{ fontWeight: 300, fontSize: '1.5rem', margin: '0 0 0.35rem' }}>Move to a new home</h2>
      <p style={{ color: colors.textSecondary, margin: '0 0 1rem', lineHeight: 1.45 }}>
        Your current home stays in Pacarion as a record you can still open. The questions run again for the new area.
      </p>
      <label style={{ display: 'block', fontWeight: 650, marginBottom: '0.35rem' }} htmlFor="move-address">
        Street address
      </label>
      <input
        id="move-address"
        value={address}
        onChange={(event) => setAddress(event.target.value)}
        autoComplete="street-address"
        style={fieldStyle(colors)}
      />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '0.75rem', marginTop: '0.75rem' }}>
        <div>
          <label style={{ display: 'block', fontWeight: 650, marginBottom: '0.35rem' }} htmlFor="move-city">City</label>
          <input id="move-city" value={city} onChange={(event) => setCity(event.target.value)} style={fieldStyle(colors)} />
        </div>
        <div>
          <label style={{ display: 'block', fontWeight: 650, marginBottom: '0.35rem' }} htmlFor="move-state">State</label>
          <select id="move-state" value={state} onChange={(event) => setState(event.target.value)} style={fieldStyle(colors)}>
            {STATES.map((code) => (
              <option key={code} value={code}>{stateName(code)}</option>
            ))}
          </select>
        </div>
        <div>
          <label style={{ display: 'block', fontWeight: 650, marginBottom: '0.35rem' }} htmlFor="move-zip">ZIP code</label>
          <input
            id="move-zip"
            inputMode="numeric"
            value={zip}
            maxLength={10}
            onChange={(event) => setZip(event.target.value)}
            style={fieldStyle(colors)}
          />
        </div>
      </div>
      <CountyField
        zip={zip}
        state={state}
        fips={countyFips}
        source={countySource}
        colors={colors}
        onChange={(county) => {
          if (!county) return;
          if (Object.prototype.hasOwnProperty.call(county, 'county_pending')) {
            setCountyPending(!!county.county_pending);
          }
          if (Object.prototype.hasOwnProperty.call(county, 'property_county_fips')) {
            setCountyFips(county.property_county_fips || '');
            setCountySource(county.property_county_source || '');
          }
        }}
      />

      <h3 style={{ fontWeight: 650, fontSize: '1.05rem', margin: '1.25rem 0 0.6rem' }}>Sponsor</h3>
      {sponsored ? (
        <button type="button" style={choiceStyle(sponsor === 'keep', colors, brandColor)} onClick={() => setSponsor('keep')}>
          <span>Keep my current sponsor</span>
        </button>
      ) : null}
      <button type="button" style={choiceStyle(sponsor === 'invite', colors, brandColor)} onClick={() => setSponsor('invite')}>
        <span>I have a new sponsor code</span>
      </button>
      {sponsor === 'invite' ? (
        <input
          aria-label="New sponsor code"
          value={inviteCode}
          onChange={(event) => setInviteCode(sponsorCodeValue(event.target.value))}
          onPaste={(event) => {
            event.preventDefault();
            const text = event.clipboardData ? event.clipboardData.getData('text') : '';
            setInviteCode(sponsorCodeValue(text));
          }}
          autoCapitalize="characters"
          autoComplete="off"
          spellCheck={false}
          style={{ ...fieldStyle(colors), marginBottom: '0.6rem' }}
        />
      ) : null}
      <button type="button" style={choiceStyle(sponsor === 'sponsorless', colors, brandColor)} onClick={() => setSponsor('sponsorless')}>
        <span>Continue without a sponsor. 30 days are included.</span>
      </button>

      {error ? <p role="alert" style={{ color: '#b91c1c' }}>{error}</p> : null}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', marginTop: '0.5rem' }}>
        <button
          type="submit"
          disabled={busy}
          style={{
            minHeight: '52px',
            border: 'none',
            borderRadius: '0.75rem',
            background: brandColor,
            color: '#fff',
            fontWeight: 700,
            fontSize: '1rem',
            fontFamily: 'inherit',
            cursor: 'pointer',
          }}
        >
          {busy ? 'Saving…' : 'Move to this home'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          style={{
            minHeight: '48px',
            border: 'none',
            background: 'transparent',
            color: colors.textSecondary,
            fontSize: '1rem',
            fontFamily: 'inherit',
            cursor: 'pointer',
          }}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
